import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { 
  QrCode, 
  Smartphone, 
  Share2, 
  BarChart3, 
  Shield, 
  Zap,
  ArrowRight,
  CheckCircle2
} from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_4935773c-183d-4ff8-bc9d-3030c509887e/artifacts/mknxgjj1_identra_logo_3d_clean_hd.jpg";

const LandingPage = () => {
  const navigate = useNavigate();
  const [hoveredFeature, setHoveredFeature] = useState(null);

  const features = [
    {
      icon: QrCode,
      title: 'Smart QR Codes',
      description: 'Generate customizable QR codes with your branding and logo embedded.'
    },
    {
      icon: Smartphone,
      title: 'NFC Ready',
      description: 'Program NFC cards for seamless tap-to-share contact exchange.'
    },
    {
      icon: Share2,
      title: 'Instant Sharing',
      description: 'Share your digital card via link, QR code, or NFC with one tap.'
    },
    {
      icon: BarChart3,
      title: 'Analytics',
      description: 'Track who views your card with detailed scan analytics.'
    },
    {
      icon: Shield,
      title: 'Secure',
      description: 'Your data is protected with enterprise-grade security.'
    },
    {
      icon: Zap,
      title: 'Fast Setup',
      description: 'Create your professional digital card in under 2 minutes.'
    }
  ];

  const benefits = [
    'Unlimited digital business cards',
    'Custom QR codes with logo',
    'NFC card programming support',
    'Real-time scan analytics',
    'vCard export for contacts',
    'Print-ready card designs'
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <img src={LOGO_URL} alt="Identra" className="h-24 w-auto" />
            </div>
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost" 
                onClick={() => navigate('/login')}
                className="text-slate-600 hover:text-slate-900"
                data-testid="nav-login-btn"
              >
                Sign In
              </Button>
              <Button 
                onClick={() => navigate('/register')}
                className="bg-[#16335E] hover:bg-[#0A1A34] text-white"
                data-testid="nav-register-btn"
              >
                Get Started
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="space-y-8">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#16335E]/5 rounded-full">
                <span className="w-2 h-2 bg-[#00A78B] rounded-full animate-pulse" />
                <span className="text-sm font-medium text-[#16335E]">Digital Identity Solutions</span>
              </div>
              
              <h1 className="text-5xl lg:text-6xl font-bold text-slate-900 leading-tight">
                Your Professional
                <span className="block text-[#16335E]">Digital Identity</span>
              </h1>
              
              <p className="text-xl text-slate-600 leading-relaxed max-w-lg">
                Create stunning digital business cards with QR codes and NFC technology. 
                Share your contact information instantly and track engagement.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <Button 
                  size="lg"
                  onClick={() => navigate('/register')}
                  className="bg-[#16335E] hover:bg-[#0A1A34] text-white px-8 h-14 text-lg gap-2"
                  data-testid="hero-get-started-btn"
                >
                  Create Your Card
                  <ArrowRight className="w-5 h-5" />
                </Button>
                <Button 
                  size="lg"
                  variant="outline"
                  onClick={() => navigate('/login')}
                  className="border-slate-300 text-slate-700 hover:bg-slate-50 px-8 h-14 text-lg"
                  data-testid="hero-login-btn"
                >
                  Sign In
                </Button>
              </div>

              <div className="flex items-center gap-8 pt-4">
                <div className="text-center">
                  <div className="text-3xl font-bold text-[#16335E]">10k+</div>
                  <div className="text-sm text-slate-500">Cards Created</div>
                </div>
                <div className="w-px h-12 bg-slate-200" />
                <div className="text-center">
                  <div className="text-3xl font-bold text-[#16335E]">50k+</div>
                  <div className="text-sm text-slate-500">Scans</div>
                </div>
                <div className="w-px h-12 bg-slate-200" />
                <div className="text-center">
                  <div className="text-3xl font-bold text-[#16335E]">99%</div>
                  <div className="text-sm text-slate-500">Uptime</div>
                </div>
              </div>
            </div>

            {/* Hero Visual */}
            <div className="relative hidden lg:block">
              <div className="absolute inset-0 bg-gradient-to-br from-[#16335E]/10 to-[#00A78B]/10 rounded-3xl transform rotate-3" />
              <div className="relative bg-white rounded-3xl shadow-2xl p-8 transform -rotate-3 hover:rotate-0 transition-transform duration-500">
                <div className="bg-gradient-to-br from-[#16335E] to-[#0A1A34] rounded-2xl p-6 text-white">
                  <div className="flex items-start justify-between mb-6">
                    <div>
                      <h3 className="text-xl font-bold">John Smith</h3>
                      <p className="text-white/80 text-sm">Senior Product Designer</p>
                      <p className="text-white/60 text-xs">TechCorp Inc.</p>
                    </div>
                    <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center">
                      <span className="text-2xl font-bold">JS</span>
                    </div>
                  </div>
                  <div className="space-y-2 text-sm mb-6">
                    <div className="flex items-center gap-2 text-white/90">
                      <span>john@techcorp.com</span>
                    </div>
                    <div className="flex items-center gap-2 text-white/90">
                      <span>+1 555-123-4567</span>
                    </div>
                  </div>
                  <div className="flex justify-center">
                    <div className="bg-white p-2 rounded-lg">
                      <QrCode className="w-16 h-16 text-[#16335E]" />
                    </div>
                  </div>
                </div>
                <div className="mt-4 flex items-center justify-center gap-2 text-slate-500 text-sm">
                  <Smartphone className="w-4 h-4" />
                  <span>Tap or scan to connect</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 px-6 lg:px-8 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-4">
              Everything You Need
            </h2>
            <p className="text-xl text-slate-600 max-w-2xl mx-auto">
              Powerful features to create, share, and track your digital business cards
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <div 
                  key={index}
                  className={`bg-white rounded-2xl p-8 transition-all duration-300 ${
                    hoveredFeature === index 
                      ? 'shadow-xl transform -translate-y-1' 
                      : 'shadow-sm hover:shadow-md'
                  }`}
                  onMouseEnter={() => setHoveredFeature(index)}
                  onMouseLeave={() => setHoveredFeature(null)}
                >
                  <div className={`w-14 h-14 rounded-xl flex items-center justify-center mb-6 transition-colors ${
                    hoveredFeature === index 
                      ? 'bg-[#16335E] text-white' 
                      : 'bg-[#16335E]/10 text-[#16335E]'
                  }`}>
                    <Icon className="w-7 h-7" />
                  </div>
                  <h3 className="text-xl font-semibold text-slate-900 mb-3">{feature.title}</h3>
                  <p className="text-slate-600">{feature.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-6">
                Why Choose Identra?
              </h2>
              <p className="text-xl text-slate-600 mb-8">
                Join thousands of professionals who have upgraded their networking with digital business cards.
              </p>
              <ul className="space-y-4">
                {benefits.map((benefit, index) => (
                  <li key={index} className="flex items-center gap-3">
                    <CheckCircle2 className="w-6 h-6 text-[#00A78B] flex-shrink-0" />
                    <span className="text-slate-700">{benefit}</span>
                  </li>
                ))}
              </ul>
              <Button 
                size="lg"
                onClick={() => navigate('/register')}
                className="mt-8 bg-[#16335E] hover:bg-[#0A1A34] text-white px-8 h-14 text-lg gap-2"
                data-testid="benefits-get-started-btn"
              >
                Get Started Free
                <ArrowRight className="w-5 h-5" />
              </Button>
            </div>

            <div className="relative">
              <div className="bg-gradient-to-br from-[#16335E] to-[#0A1A34] rounded-3xl p-8 lg:p-12">
                <div className="text-center text-white">
                  <div className="text-6xl font-bold mb-2">Free</div>
                  <div className="text-xl text-white/80 mb-8">to get started</div>
                  <ul className="space-y-3 text-left max-w-xs mx-auto">
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="w-5 h-5 text-[#00A78B]" />
                      <span>Create unlimited cards</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="w-5 h-5 text-[#00A78B]" />
                      <span>Custom QR codes</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="w-5 h-5 text-[#00A78B]" />
                      <span>NFC card support</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="w-5 h-5 text-[#00A78B]" />
                      <span>Analytics dashboard</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="w-5 h-5 text-[#00A78B]" />
                      <span>Print-ready designs</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6 lg:px-8 bg-[#16335E]">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl lg:text-4xl font-bold text-white mb-6">
            Ready to Transform Your Networking?
          </h2>
          <p className="text-xl text-white/80 mb-8">
            Create your professional digital identity in minutes. No credit card required.
          </p>
          <Button 
            size="lg"
            onClick={() => navigate('/register')}
            className="bg-[#00A78B] hover:bg-[#018F76] text-[#16335E] font-semibold px-12 h-14 text-lg"
            data-testid="cta-get-started-btn"
          >
            Create Your Free Card
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 lg:px-8 bg-slate-900">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-3">
              <img src={LOGO_URL} alt="Identra" className="h-8 w-auto brightness-0 invert" />
            </div>
            <div className="text-slate-400 text-sm text-center">
              © {new Date().getFullYear()} Identra. All rights reserved.
            </div>
            <div className="flex items-center gap-3">
              <span className="text-slate-400 text-sm">A product by</span>
              <a 
                href="https://www.ithr.tech" 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center"
              >
                <img 
                  src="https://customer-assets.emergentagent.com/job_679c6880-e28f-4852-b622-6b77cb70fca4/artifacts/1im6xoy9_ITHR_Logo_Mono_White.svg" 
                  alt="ITHR Technologies Consulting LLC" 
                  className="h-6 w-auto"
                />
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
